import { Navigate, Outlet } from "react-router-dom";
import { useUserStore } from "../store/userStore";
import { ADMIN_RESELLER_ROLE_ID } from "../hooks/useIsReseller";
import {
  PTAR_COMPANY_ID,
  getPtarAllowedApps,
} from "../hooks/useTruckYardRole";

interface PtarAppProtectedRouteProps {
  /** App id being guarded ("truck-yard" or "inspecciones"). */
  appId: "truck-yard" | "inspecciones";
  redirectTo?: string;
}

/**
 * Route-level guard for the PTAR-exclusive apps (Patio de Maniobras /
 * Inspecciones). It mirrors the sidebar visibility rules in
 * `useFilteredCategories`, so a direct URL / bookmark / lastRoute restore
 * cannot bypass what the AppsSidebar hides:
 *
 *  - Admin Reseller: full access to both apps.
 *  - Non-PTAR company users: blocked (these apps are PTAR-exclusive).
 *  - PTAR company users: gated by their truck-yard role -> allowed apps
 *      · Admin (compañía) -> truck-yard + inspecciones
 *      · Yard admin       -> truck-yard + inspecciones (#558)
 *      · Supervisor       -> truck-yard + inspecciones
 *      · Inspector        -> inspecciones only
 *      · Guard            -> truck-yard only
 *    A PTAR user with no truck-yard role has no per-app restriction (same as
 *    the sidebar), so both apps remain reachable for them.
 */
export default function PtarAppProtectedRoute({
  appId,
  redirectTo = "/map",
}: PtarAppProtectedRouteProps) {
  const user = useUserStore((state) => state.user);

  // Reactive user store with localStorage fallback (same as useFilteredCategories).
  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  const userCompanyId =
    user?.company_id ||
    localStorage.getItem("userCompanyId") ||
    localStorage.getItem("companyId") ||
    "";

  // Admin Reseller bypasses every PTAR restriction.
  if (userRoleId === ADMIN_RESELLER_ROLE_ID) {
    return <Outlet />;
  }

  // These apps are exclusive to the PTAR company.
  if (userCompanyId !== PTAR_COMPANY_ID) {
    return <Navigate to={redirectTo} replace />;
  }

  // Within PTAR, gate by truck-yard role -> allowed apps. La tabla de
  // useTruckYardRole es la misma que usa el sidebar: eran dos cadenas de
  // `if/else if` escritas a mano sobre los mismos ids, y mantenerlas
  // sincronizadas a ojo es lo que dejó al rol "yard admin" fuera de las dos
  // (#558).
  const allowedApps = getPtarAllowedApps(userCompanyId, userRoleId);

  if (allowedApps && !allowedApps.includes(appId)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}
